// The money summary under a cart or an order: subtotal, tax and total.

export interface PricedLine {
  quantity: number;
  /** The line's posted price, as stored (text) or parsed. Blank means no price. */
  price?: string | number | null;
  taxPercent?: string | number | null;
}

export interface OrderTotals {
  subtotal: number;
  tax: number;
  total: number;
}

const toNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Totals are shown only when EVERY line being ordered has a price. A sum over
 * the priced lines alone reads as the order's total and understates it, so with
 * even one unpriced line there is no summary at all — each priced line still
 * shows its own posted price. Lines at quantity 0 (taken out of the cart) are
 * not being ordered and do not count either way.
 */
export function orderTotals(lines: PricedLine[]): OrderTotals | null {
  const ordered = lines.filter((line) => line.quantity > 0);
  if (ordered.length === 0) return null;
  if (ordered.some((line) => !(toNumber(line.price) > 0))) return null;

  let subtotal = 0;
  let tax = 0;
  for (const line of ordered) {
    const amount = toNumber(line.price) * line.quantity;
    subtotal += amount;
    tax += (amount * toNumber(line.taxPercent)) / 100;
  }
  return { subtotal, tax, total: subtotal + tax };
}
